import { EventSubscriber, } from '@verdant-web/common';
import { Heartbeat } from './Heartbeat.js';
export class PushPullSync extends EventSubscriber {
    constructor({ endpointProvider, meta, presence, interval = 15 * 1000, log = () => { }, }) {
        super();
        this.mode = 'pull';
        this._isConnected = false;
        this._status = 'paused';
        this._hasSynced = false;
        this.setInterval = (interval) => {
            this.heartbeat.setInterval(interval);
        };
        this.sendRequest = async (messages) => {
            this.log('Sending sync request', messages);
            try {
                const { http: host, token } = await this.endpointProvider.getEndpoints();
                const response = await fetch(host, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        messages,
                    }),
                    credentials: 'include',
                });
                if (response.ok) {
                    this.heartbeat.keepAlive();
                    const json = (await response.json());
                    for (const message of json.messages) {
                        this.handleServerMessage(message);
                    }
                    if (!this._isConnected) {
                        this._isConnected = true;
                        this.emit('onlineChange', true);
                    }
                }
                else {
                    this.log('Sync request failed', response.status, await response.text());
                    if (this._isConnected) {
                        this._isConnected = false;
                        this.emit('onlineChange', false);
                    }
                    // only keep trying if the error was not 4xx
                    if (response.status >= 500) {
                        this.heartbeat.keepAlive();
                    }
                }
            }
            catch (error) {
                if (this._isConnected) {
                    this._isConnected = false;
                    this.emit('onlineChange', false);
                }
                this.log(error);
                this.heartbeat.keepAlive();
            }
        };
        this.handleServerMessage = async (message) => {
            if (message.type === 'sync-resp') {
                if (message.ackThisNonce) {
                    // we need to ack the nonce to confirm that we received the sync-resp
                    this.log('Sending sync ack', message.ackThisNonce);
                    this.sendRequest([
                        await this.meta.messageCreator.createAck(message.ackThisNonce),
                    ]);
                }
                // but we can go ahead and preemptively allow ops to be sent
                this._hasSynced = true;
            }
            this.emit('message', message);
        };
        this.send = (message) => {
            // only certain messages are sent for pull-based sync.
            switch (message.type) {
                case 'presence-update':
                case 'sync':
                case 'heartbeat':
                    return this.sendRequest([message]);
                case 'op':
                    if (this._hasSynced) {
                        return this.sendRequest([message]);
                    }
                    break;
            }
        };
        this.dispose = () => { };
        // on a heartbeat, do a sync
        this.onHeartbeat = async () => {
            // for HTTP sync we send presence first, so that the sync-resp message
            // will include the client's own presence info and fill in missing profile
            // data on the first request. otherwise it would have to wait for the second.
            this.sendRequest([
                await this.meta.messageCreator.createPresenceUpdate(this.presence.self.presence),
                await this.meta.messageCreator.createSyncStep1(),
            ]);
        };
        // if the server fails to respond in a certain amount of time, we assume
        // the connection is lost and go offline.
        this.onHeartbeatMissed = async () => {
            this.emit('onlineChange', false);
            this.log('Missed heartbeat');
            this._isConnected = false;
        };
        this.log = log;
        this.meta = meta;
        this.presence = presence;
        this.endpointProvider = endpointProvider;
        this.heartbeat = new Heartbeat({
            interval,
        });
        this.heartbeat.subscribe('beat', this.onHeartbeat);
        this.heartbeat.subscribe('missed', this.onHeartbeatMissed);
    }
    get interval() {
        return this.heartbeat.interval;
    }
    start() {
        if (this.status === 'active') {
            return;
        }
        this.heartbeat.start(true);
        this._status = 'active';
    }
    stop() {
        this.heartbeat.stop();
        this._status = 'paused';
    }
    reconnect() {
        this.heartbeat.start(true);
    }
    get isConnected() {
        return this._isConnected;
    }
    get status() {
        return this._status;
    }
}
//# sourceMappingURL=PushPullSync.js.map